import React, { Component } from 'react';
import { observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { ColorRing } from 'react-loader-spinner';
import moment from 'moment';

import PageTitle from '../../../components/common/PageTitle';
import {
  Row, Col,
  Card, CardHeader, CardBody,
  Button, ButtonGroup, FormSelect
} from 'shards-react';
import './widgetAnalytics.scss';

// 위젯 필터 옵션 (백엔드 widget 값과 일치)
const WIDGET_OPTIONS = [
  { key: '', label: '전체 위젯' },
  { key: 'product_list', label: '상품상세 리뷰' },
  { key: 'viewer', label: '리뷰 뷰어' },
  { key: 'grid', label: '그리드' },
  { key: 'board_page', label: '리뷰 게시판' }
];

const WIDGET_LABEL = {
  product_list: '상품상세 리뷰', viewer: '리뷰 뷰어', grid: '그리드', board_page: '리뷰 게시판'
};

// 스토어 페이지 타입 라벨 (widget 트래커의 curPage 값과 일치)
const PAGE_LABEL = {
  product: '상품상세', category: '카테고리/목록', index: '메인', board: '리뷰 게시판',
  cart: '장바구니', order: '주문', orderList: '주문내역', orderDetail: '주문상세',
  mypage: '마이페이지', search: '검색', blog: '블로그'
};

// 참여 이벤트 표기 (백엔드 engagement 키와 일치)
const ENGAGEMENT_META = [
  { key: 'photo_click', label: '사진 클릭', emoji: '📷' },
  { key: 'sort_change', label: '정렬 변경', emoji: '↕️' },
  { key: 'ai_keyword', label: 'AI 키워드', emoji: '🏷️' },
  { key: 'helpful_click', label: '도움돼요', emoji: '👍' },
  { key: 'write_click', label: '리뷰 작성 클릭', emoji: '✍️' }
];

/**
 * 행동 분석 (입점사 대면)
 * - 쇼퍼의 리뷰 위젯 행동 퍼널: 위젯 노출 → 리뷰 클릭 → 리뷰 상세 → 구매의도 클릭
 * - store_id 는 백엔드에서 인증 토큰(selStore)으로 스코프. 슈퍼어드민은 스토어 전환으로 열람.
 * - 데이터 출처: widgetEventLog (getWidgetFunnel). 구매 전환(Order 조인)은 2차.
 */
@inject(({ widget }) => ({ getWidgetFunnel: widget.getWidgetFunnel }))
@inject(({ teamUser }) => ({ me: teamUser.me }))
@observer
class WidgetAnalytics extends Component {

  @observable isLoading = true;
  @observable periodDays = 14;   // 7 | 14 | 30
  @observable widgetFilter = '';
  @observable pageFilter = '';
  @observable result = null;

  componentDidMount() {
    this.load();
  }

  @action setPeriod = (d) => { this.periodDays = d; this.load(); };
  @action setWidget = (w) => { this.widgetFilter = w; this.load(); };
  @action setPage = (p) => { this.pageFilter = p; this.load(); };

  @action load = () => {
    this.isLoading = true;
    const params = {
      startDate: moment().subtract(this.periodDays - 1, 'days').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      widget: this.widgetFilter || undefined,
      page: this.pageFilter || undefined
    };
    this.props.getWidgetFunnel(params, (data) => {
      this.result = data;
      this.isLoading = false;
    });
  };

  render() {
    const r = this.result;
    const funnel = (r && r.funnel) || [];

    return (
      <div style={{ padding: '0px 16px 30px' }} className="wa-page">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} className="mb-4">
          <PageTitle title="행동 분석" subtitle="리뷰 위젯 → 구매 흐름" className="mb-0" />
        </div>

        {/* === 필터 바 === */}
        <Card className="mb-3">
          <CardBody className="wa-filters">
            <div className="wa-filter-group">
              <span className="wa-filter-label">기간 (오늘 기준 최근)</span>
              <ButtonGroup size="sm">
                {[7, 14, 30].map(d => (
                  <Button key={d} theme={this.periodDays === d ? 'primary' : 'light'}
                          onClick={() => this.setPeriod(d)}>{d}일</Button>
                ))}
              </ButtonGroup>
            </div>
            <div className="wa-filter-group">
              <span className="wa-filter-label">위젯</span>
              <FormSelect size="sm" value={this.widgetFilter}
                          onChange={e => this.setWidget(e.target.value)} style={{ width: 160 }}>
                {WIDGET_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </FormSelect>
            </div>
            <div className="wa-filter-group">
              <span className="wa-filter-label">페이지</span>
              <FormSelect size="sm" value={this.pageFilter}
                          onChange={e => this.setPage(e.target.value)} style={{ width: 150 }}>
                <option value="">전체 페이지</option>
                {Object.keys(PAGE_LABEL).map(k => <option key={k} value={k}>{PAGE_LABEL[k]}</option>)}
              </FormSelect>
            </div>
            {r && <span className="wa-period-range">{r.startDate} ~ {r.endDate}</span>}
          </CardBody>
        </Card>

        {this.isLoading ? (
          <div className="wa-loading">
            <ColorRing height="60" width="60" colors={['#6D28D9', '#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE']} />
          </div>
        ) : !r || !r.totalEvents ? (
          <Card><CardBody className="wa-empty">
            아직 수집된 행동 데이터가 없습니다.<br />위젯 배포 후 방문자 행동이 발생하면 이곳에 표시됩니다.
          </CardBody></Card>
        ) : (
          <>
            {/* 요약 */}
            <Row>
              <Col lg="6" sm="6" className="mb-3">
                <Card className="wa-summary"><CardBody>
                  <div className="wa-summary-num">{(r.totalVisitors || 0).toLocaleString()}</div>
                  <div className="wa-summary-label">방문자 수 (고유)</div>
                </CardBody></Card>
              </Col>
              <Col lg="6" sm="6" className="mb-3">
                <Card className="wa-summary"><CardBody>
                  <div className="wa-summary-num">{(r.totalEvents || 0).toLocaleString()}</div>
                  <div className="wa-summary-label">행동 이벤트 수</div>
                </CardBody></Card>
              </Col>
            </Row>

            {/* 퍼널 */}
            <Card className="mb-3">
              <CardHeader className="border-bottom"><h6 className="m-0">구매 전환 퍼널</h6></CardHeader>
              <CardBody>
                <div className="wa-funnel">
                  {funnel.map((s, i) => (
                    <div className="wa-step" key={s.key}>
                      <div className="wa-step-head">
                        <span className="wa-step-label">{i + 1}. {s.label}</span>
                        <span className="wa-step-visitors">{(s.visitors || 0).toLocaleString()}명</span>
                      </div>
                      <div className="wa-step-bar-wrap">
                        <div className="wa-step-bar" style={{ width: Math.max(s.fromImpression || 0, 2) + '%' }} />
                        <span className="wa-step-pct">{s.fromImpression}%</span>
                      </div>
                      {i > 0 && <div className="wa-step-drop">직전 단계 대비 {s.stepRate}%</div>}
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>

            {/* 참여 이벤트 + 위젯별 + 페이지별 */}
            <Row>
              <Col lg="4" md="6" className="mb-3">
                <Card><CardHeader className="border-bottom"><h6 className="m-0">참여 이벤트</h6></CardHeader>
                  <CardBody><ul className="wa-list">
                    {ENGAGEMENT_META.map(m => (
                      <li key={m.key}>
                        <span className="wa-list-label">{m.emoji} {m.label}</span>
                        <span className="wa-list-count">{((r.engagement && r.engagement[m.key]) || 0).toLocaleString()}</span>
                      </li>
                    ))}
                  </ul></CardBody>
                </Card>
              </Col>
              <Col lg="4" md="6" className="mb-3">
                <Card><CardHeader className="border-bottom"><h6 className="m-0">위젯별 이벤트</h6></CardHeader>
                  <CardBody><ul className="wa-list">
                    {Object.keys(r.byWidget || {}).length === 0
                      ? <li className="wa-none">데이터 없음</li>
                      : Object.keys(r.byWidget).map(w => (
                        <li key={w}>
                          <span className="wa-list-label">{WIDGET_LABEL[w] || w}</span>
                          <span className="wa-list-count">{(r.byWidget[w] || 0).toLocaleString()}</span>
                        </li>
                      ))}
                  </ul></CardBody>
                </Card>
              </Col>
              <Col lg="4" md="6" className="mb-3">
                <Card><CardHeader className="border-bottom"><h6 className="m-0">페이지별 이벤트</h6></CardHeader>
                  <CardBody><ul className="wa-list">
                    {Object.keys(r.byPage || {}).length === 0
                      ? <li className="wa-none">데이터 없음</li>
                      : Object.keys(r.byPage).sort((x, y) => r.byPage[y] - r.byPage[x]).map(pg => (
                        <li key={pg}>
                          <span className="wa-list-label">{PAGE_LABEL[pg] || pg}</span>
                          <span className="wa-list-count">{(r.byPage[pg] || 0).toLocaleString()}</span>
                        </li>
                      ))}
                  </ul></CardBody>
                </Card>
              </Col>
            </Row>

            {/* 노출 상위 상품 */}
            <Card className="mb-3">
              <CardHeader className="border-bottom"><h6 className="m-0">노출 상위 상품</h6></CardHeader>
              <CardBody className="p-0">
                <table className="table mb-0 wa-table">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: 44 }}>#</th>
                      <th style={{ width: 60 }}>썸네일</th>
                      <th>상품명</th>
                      <th>상품 ID</th>
                      <th className="text-right">노출수</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(r.topProducts || []).length === 0
                      ? <tr><td colSpan="5" className="wa-none">데이터 없음</td></tr>
                      : r.topProducts.map((p, i) => (
                        <tr key={p.platformProductId || i}>
                          <td>{i + 1}</td>
                          <td>
                            {p.thumbnail
                              ? <img src={p.thumbnail} alt="" className="wa-thumb" loading="lazy" />
                              : <span className="wa-thumb wa-thumb--empty" />}
                          </td>
                          <td className="wa-pname" title={p.productName || ''}>{p.productName || '-'}</td>
                          <td className="wa-pid">{p.platformProductId || '-'}</td>
                          <td className="text-right">{(p.impressions || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </>
        )}
      </div>
    );
  }
}

export default WidgetAnalytics;
